import { Component, Input, OnInit } from '@angular/core';
import { Chippable } from '@app/interfaces';
import { CustomReportFilter } from '@app/models/reporting/custom-report-filter.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { FeatureService } from '@app/services/feature.service';
import moment, { Moment } from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_RANGE_VALUES = 2;
export interface CustomFilter {
    label: string;
    propertyName: string;
    operators: { [key: string]: string };
    filterType: string;
    filterOptions: { [key: string]: string };
}

export interface Filter {
    selectedCategory: CustomFilter;
    selectedFilterOptions: Chippable[];
    selectedDate?: Date;
    selectedDateRange?: { begin?: Date; end?: Date };
    data: CustomReportFilter;
}

const createEmptyFilter = (): Filter => {
    const emptyFilter: Filter = {
        selectedCategory: {
            label: '',
            propertyName: '',
            operators: {},
            filterType: '',
            filterOptions: {},
        },
        selectedFilterOptions: [],
        selectedDate: null,
        selectedDateRange: null,
        data: new CustomReportFilter(),
    };

    return emptyFilter;
};

const clearFilter = (filter: Filter): void => {
    filter.data.operator = '';
    filter.selectedFilterOptions = [];
    filter.data.value = [];
};

const convertFilterOptionsToChipLabel = (filterOptions: { [key: string]: string }): Chippable[] => {
    return Object.keys(filterOptions).map((option) => ({
        chipLabel: filterOptions[option],
    }));
};

const filterOptionsBasedOnKeys = (options: { [key: string]: string }, keys: string[]): { [key: string]: string } => {
    return keys.reduce((result, key) => ({ ...result, [key]: options[key] }), {});
};
@Component({
    selector: 'app-custom-report-filters',
    templateUrl: './custom-report-filters.form.html',
    styleUrls: ['./custom-report-filters.style.scss'],
})
export class CustomReportFiltersForm extends BaseForm implements OnInit {
    @Input() initalCustomReportFilters: CustomReportFilter[] = [];

    isLoading = true;
    customReportFilter: CustomReportFilter = new CustomReportFilter();

    allFilters: CustomFilter[] = [];

    defaultFilterObject: Filter = {
        selectedCategory: {
            label: 'Status',
            propertyName: 'employees.status',
            operators: {
                is: 'Is',
                isNot: 'Is not',
                isBlank: 'Is blank',
            },
            filterType: 'string',
            filterOptions: {
                active: 'active',
                onboarding: 'onboarding',
                terminated: 'terminated',
                'on leave': 'on leave',
            },
        },
        selectedFilterOptions: [{ chipLabel: 'active' }],
        data: new CustomReportFilter(),
    };

    selectedFilters: Filter[] = [];

    selectedCategoryChips: Chippable[] = [];
    chipsAutoCompleteLabel = '';

    constructor(private readonly featureService: FeatureService) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.getFilters();
    }

    getSelectedFilters(): Filter[] {
        return this.selectedFilters;
    }

    addNewFilter(): void {
        this.selectedFilters.push(createEmptyFilter());
    }

    deleteFilter(index: number): void {
        this.selectedFilters.splice(index, 1);
    }

    onCategorySelectionChange(currentFilter: Filter): void {
        currentFilter.data.property = currentFilter.selectedCategory.propertyName;
        clearFilter(currentFilter);
    }

    clearValues(currentFilter: Filter): void {
        currentFilter.data.value = [];
    }

    getPlaceholderOperatorOfSelectedCategory(currentFilter: Filter): string {
        if (currentFilter.data.operator) {
            return;
        }

        const defaultOperator = Object.keys(currentFilter.selectedCategory.operators)[0];
        currentFilter.data.operator = defaultOperator;
        return defaultOperator;
    }

    getOperatorsOfSelectedCategory(currentFilter: Filter): string[] {
        return Object.keys(currentFilter.selectedCategory.operators);
    }

    getFilterChips(currentFilter: Filter): Chippable[] {
        if (this.isDateFilter(currentFilter)) {
            return;
        }
        this.selectedCategoryChips = convertFilterOptionsToChipLabel(currentFilter.selectedCategory.filterOptions);
        return this.selectedCategoryChips;
    }

    getSelectedFilterChips(currentFilter: Filter): Chippable[] {
        if (currentFilter.selectedFilterOptions) {
            this.convertSelectedFilterChipsToIds(currentFilter);
        }

        return currentFilter.selectedFilterOptions;
    }

    isDateFilter(currentFilter: Filter): boolean {
        return currentFilter.selectedCategory.filterType === 'date';
    }

    isOperatorBlank(currentFilter: Filter): boolean {
        return currentFilter.data.operator === 'isBlank';
    }

    isOperatorNull(currentFilter: Filter): boolean {
        return currentFilter.data.operator === '' || currentFilter.data.operator === null;
    }

    isOperatorBetween(currentFilter: Filter): boolean {
        return currentFilter.data.operator === 'between';
    }

    onDateChange(date: Moment, currentFilter: Filter): void {
        const selectedDate = date.format(DATE_FORMAT);

        currentFilter.data.value = [selectedDate];
    }

    onDateRangeChange(begin: Moment, end: Moment, currentFilter: Filter): void {
        const dateRangeComplete = begin && end;

        if (!dateRangeComplete) {
            return;
        }

        const startAt = begin.format(DATE_FORMAT);
        const endAt = end.format(DATE_FORMAT);

        currentFilter.data.value = [startAt, endAt];
    }

    compareWith(category: { [key: string]: string }, selectedCategory: { [key: string]: string }): boolean {
        return category && selectedCategory && category.propertyName === selectedCategory.propertyName;
    }

    isAddFilterDisabled(): boolean {
        if (!this.selectedFilters.length) {
            return;
        }
        return !this.selectedFilters[this.selectedFilters.length - 1].selectedCategory.label;
    }

    showEmptyDiv(currentFilter: Filter): boolean {
        return (
            this.isOperatorBlank(currentFilter) ||
            (this.isDateFilter(currentFilter) && this.isOperatorNull(currentFilter))
        );
    }

    private getFilters(): void {
        this.customReportFilter
            .getFilters()
            .then((customReportFilters) => {
                this.allFilters = customReportFilters.data;
                this.setSelectedFilters();

                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    private setSelectedFilters(): void {
        if (this.initalCustomReportFilters.length === 0) {
            this.setDefaultFilter();

            this.selectedFilters.push(this.defaultFilterObject);
            return;
        }

        this.selectedFilters = this.initalCustomReportFilters.map((initialFilter) => {
            const selectedFilter = createEmptyFilter();

            selectedFilter.selectedCategory = this.allFilters.find(
                (category) => category.propertyName === initialFilter.property
            );

            if (!this.isDateFilter(selectedFilter)) {
                selectedFilter.selectedFilterOptions = convertFilterOptionsToChipLabel(
                    filterOptionsBasedOnKeys(selectedFilter.selectedCategory.filterOptions, initialFilter.value)
                );
            }

            selectedFilter.data = initialFilter;

            if (this.isDateFilter(selectedFilter)) {
                this.setSelectedDate(initialFilter, selectedFilter);
            }

            return selectedFilter;
        });
    }

    private setSelectedDate(initialFilter: CustomReportFilter, selectedFilter: Filter): void {
        if (initialFilter.value.length < DATE_RANGE_VALUES) {
            selectedFilter.selectedDate = moment(initialFilter.value[0]).toDate();
            return;
        }

        /**
         * We need to access the last tuple because the data used to be stored
         * as an array where new selections were appended, instead of replacing the current ones.
         * There are instances in the database where the current selected dates are at the
         * end of a list of dates.
         *
         * For example,
         * ["2020-12-15", "2020-12-31", "2020-12-09", "2020-12-16", "2020-12-01", "2020-12-23"]
         *
         * The actual desired start and end dates would be "2020-12-01" and "2020-12-23", respectively.
         *
         * The logic to storing these dates has been fixed here, but there is still invalid data
         * in the database so we must account for it.
         *
         * @TODO remove this complex logic once the database has been cleaned up.
         */
        const lastIndex = initialFilter.value.length;
        const lastTuple = initialFilter.value.slice(lastIndex - 2);
        const [beginDate, endDate] = lastTuple;

        selectedFilter.selectedDateRange = {
            begin: moment(beginDate).toDate(),
            end: moment(endDate).toDate(),
        };
    }

    private convertSelectedFilterChipsToIds(currentFilter: Filter): void {
        const selectedFilterChips = currentFilter.selectedFilterOptions.map((option) => option.chipLabel);
        const allFilterOptions = currentFilter.selectedCategory.filterOptions;

        currentFilter.data.value = [];
        selectedFilterChips.forEach((selectedFilterChip) => {
            const id = Object.keys(allFilterOptions).find((id) => allFilterOptions[id] === selectedFilterChip);

            if (currentFilter.data.value.indexOf(id) === -1) {
                currentFilter.data.value.push(id);
            }
        });
    }

    private setDefaultFilter(): void {
        this.defaultFilterObject.data.property = 'employees.status';
        this.defaultFilterObject.data.operator = 'is';
        this.defaultFilterObject.data.value = ['active'];
    }
}
